import { DatePipe } from "@angular/common";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

//outside sources
import { NgDatepickerModule } from "ng2-datepicker";
import { Ng4LoadingSpinnerModule } from "ng4-loading-spinner";
import { MatDialogModule } from "@angular/material";

//modules
import { AppRoutingModule } from "./modules/appRouting.module";

//services
import { AlertService } from "./services/alert.service";
import { AuthGuardService } from "./services/auth-guard.service";
import { AuthenticationService } from "./services/authentication.service";
import { BudgetService } from "./services/budget.service";
import { CacheService } from "./services/cache.service";
import { CashAdvanceService } from "./services/cash-advance.service";
import { ClientService } from "./services/client.service";
import { CommonService } from "./services/common.service";
import { DailyTripService } from "./services/daily-trip.service";
import { ExpenseService } from "./services/expense.service";
import { JwtInterceptorService } from "./services/jwt-interceptor.service";
import { LogService } from "./services/log.service";
import { MessagesService } from "./services/messages.service";
import { ParameterService } from "./services/parameter.service";
import { PayrollService } from "./services/payroll.service";
import { ReportService } from "./services/report.service";
import { SalaryService } from "./services/salary.service";
import { TripPaymentService } from "./services/trip-payment.service";
import { UserService } from "./services/user.service";
import { VehicleService } from "./services/vehicle.service";
import { VehicleCashoutService } from "./services/vehicle-cashout.service";

//components
import { AppComponent } from "./app.component";
import { BudgetFilterComponent } from "./components/budgets/budget-filter/budget-filter.component";
import { BudgetFormComponent } from "./components/budgets/budget-form/budget-form.component";
import { BudgetListComponent } from "./components/budgets/budget-list/budget-list.component";
import { CashAdvanceFilterComponent } from "./components/cashAdvances/cash-advance-filter/cash-advance-filter.component";
import { CashAdvanceFormComponent } from "./components/cashAdvances/cash-advance-form/cash-advance-form.component";
import { CashAdvanceListComponent } from "./components/cashAdvances/cash-advance-list/cash-advance-list.component";
import { ChangePasswordComponent } from "./components/change-password/change-password.component";
import { ClientCollectibleComponent } from "./components/clients/client-collectible/client-collectible.component";
import { ClientFormComponent } from "./components/clients/client-form/client-form.component";
import { ClientListComponent } from "./components/clients/client-list/client-list.component";
import { ClientTripHistoryComponent } from "./components/clients/client-trip-history/client-trip-history.component";
import { TableConfigurationComponent } from "./components/configurations/table-configuration/table-configuration.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { ConnectionFailedComponent } from "./components/errors/connection-failed/connection-failed.component";
import { NotAvailableComponent } from "./components/errors/not-available/not-available.component";
import { UnauthorizedComponent } from "./components/errors/unauthorized/unauthorized.component";
import { ExpenseFilterComponent } from "./components/expenses/expense-filter/expense-filter.component";
import { ExpenseFormComponent } from "./components/expenses/expense-form/expense-form.component";
import { ExpenseListComponent } from "./components/expenses/expense-list/expense-list.component";
import { LoginComponent } from "./components/login/login.component";
import { NavigationComponent } from "./components/navigation/navigation.component";
import { CollectiblesComponent } from "./components/reports/collectibles/collectibles.component";
import { PayrollFilterComponent } from "./components/salaries/payroll-filter/payroll-filter.component";
import { PayrollFormComponent } from "./components/salaries/payroll-form/payroll-form.component";
import { PayrollListComponent } from "./components/salaries/payroll-list/payroll-list.component";
import { SalaryFormComponent } from "./components/salaries/salary-form/salary-form.component";
import { SalaryHistoryComponent } from "./components/salaries/salary-history/salary-history.component";
import { SalaryReportComponent } from "./components/salaries/salary-report/salary-report.component";
import { ParametersComponent } from "./components/settings/parameters/parameters.component";
import { InputTypeaheadComponent } from "./components/shared/input-typeahead/input-typeahead.component";
import { LoaderComponent } from "./components/shared/loader/loader.component";
import { PayrollTypeSelectComponent } from "./components/shared/payroll-type-select/payroll-type-select.component";
import { SimpleTableComponent } from "./components/shared/simple-table/simple-table.component";
import { SnackBarComponent } from "./components/shared/snack-bar/snack-bar.component";
import { ZtsiBoolPickerComponent } from "./components/shared/ztsi-bool-picker/ztsi-bool-picker.component";
import { ZtsiBudgetTypePickerComponent } from "./components/shared/ztsi-budget-type-picker/ztsi-budget-type-picker.component";
import { ZtsiClientSelectComponent } from "./components/shared/ztsi-client-select/ztsi-client-select.component";
import { ZtsiContainerSelectComponent } from "./components/shared/ztsi-container-select/ztsi-container-select.component";
import { ZtsiContainerSizeSelectComponent } from "./components/shared/ztsi-container-size-select/ztsi-container-size-select.component";
import { ZtsiDatePickerComponent } from "./components/shared/ztsi-date-picker/ztsi-date-picker.component";
import { ZtsiExpenseTypeComponent } from "./components/shared/ztsi-expense-type-picker/ztsi-expense-type-picker.component";
import { ZtsiPagerComponent } from "./components/shared/ztsi-pager/ztsi-pager.component";
import { ZtsiPaymentTypeSelectComponent } from "./components/shared/ztsi-payment-type-select/ztsi-payment-type-select.component";
import { ZtsiRoleSelectComponent } from "./components/shared/ztsi-role-select/ztsi-role-select.component";
import { ZtsiSalaryTypeSelectComponent } from "./components/shared/ztsi-salary-type-select/ztsi-salary-type-select.component";
import { ZtsiShippingLineSelectComponent } from "./components/shared/ztsi-shipping-line-select/ztsi-shipping-line-select.component";
import { ZtsiSwitchComponent } from "./components/shared/ztsi-switch/ztsi-switch.component";
import { ZtsiTableComponent } from "./components/shared/ztsi-table/ztsi-table.component";
import { ZtsiTripSelectComponent } from "./components/shared/ztsi-trip-select/ztsi-trip-select.component";
import { ZtsiUserSelectComponent } from "./components/shared/ztsi-user-select/ztsi-user-select.component";
import { ZtsiVehiclePickerComponent } from "./components/shared/ztsi-vehicle-select/ztsi-vehicle-select.component";
import { ShippingLineFormComponent } from "./components/shippingLines/shipping-line-form/shipping-line-form.component";
import { ShippingLineListComponent } from "./components/shippingLines/shipping-line-list/shipping-line-list.component";
import { MessagesComponent } from "./components/tools/messages/messages.component";
import { DailyTripFilterComponent } from "./components/trips/daily-trip-filter/daily-trip-filter.component";
import { DailyTripFormComponent } from "./components/trips/daily-trip-form/daily-trip-form.component";
import { DailyTripListComponent } from "./components/trips/daily-trip-list/daily-trip-list.component";
import { DailyTripTicketComponent } from "./components/trips/daily-trip-ticket/daily-trip-ticket.component";
import { TripFilterComponent } from "./components/trips/trip-filter/trip-filter.component";
import { TripFormComponent } from "./components/trips/trip-form/trip-form.component";
import { TripListComponent } from "./components/trips/trip-list/trip-list.component";
import { TripPaymentFilterComponent } from "./components/trips/trip-payment-filter/trip-payment-filter.component";
import { TripPaymentFormComponent } from "./components/trips/trip-payment-form/trip-payment-form.component";
import { TripPaymentListComponent } from "./components/trips/trip-payment-list/trip-payment-list.component";
import { ProfileComponent } from "./components/users/profile/profile.component";
import { UserFilterComponent } from "./components/users/user-filter/user-filter.component";
import { UserFormComponent } from "./components/users/user-form/user-form.component";
import { UserListComponent } from "./components/users/user-list/user-list.component";
import { VehicleExpensesComponent } from "./components/vehicles/vehicle-expenses/vehicle-expenses.component";
import { VehicleFilterComponent } from "./components/vehicles/vehicle-filter/vehicle-filter.component";
import { VehicleFormComponent } from "./components/vehicles/vehicle-form/vehicle-form.component";
import { VehicleListComponent } from "./components/vehicles/vehicle-list/vehicle-list.component";
import { DisableControlDirective } from "./directives/disable-control.directive";
import { ConfigurationService } from "./services/configuration.service";
import { ShippingLineService } from "./services/shipping-line.service";
import { TripService } from "./services/trip.service";
import { CashoutFormDialogComponent } from "./components/vehicles/cashout-form-dialog/cashout-form-dialog.component";
import { ZtsiTripPickerComponent } from './components/shared/ztsi-trip-picker/ztsi-trip-picker.component';
import { ZtsiTripPickerDialogComponent } from "./components/shared/ztsi-trip-picker/ztsi-trip-picker-dialog.component";
import { ZtsiTripTypeComponent } from "./components/shared/ztsi-trip-type-select/ztsi-trip-type-select.component";
import { ZtsiDailyTripTypeComponent } from "./components/shared/ztsi-daily-trip-type-select/ztsi-daily-trip-type-select.component";
import { ZtsiDeleteConfirmationDialogComponent } from "./components/shared/ztsi-delete-confirmation-dialog/ztsi-delete-confirmation-dialog.component";
import { ZtsiTabsExpenseSelectComponent } from "./components/shared/ztsi-tabs-expense-select/ztsi-tabs-expense-select.component";
import { ZtsiTabsPenaltyExpenseSelectComponent } from "./components/shared/ztsi-tabs-penalty-expense-select/ztsi-tabs-penalty-expense-select.component";
import { TripPaymentDepositConfirmationDialogComponent } from "./components/trips/trip-payment-deposit-confirmation-dialog/trip-payment-deposit-confirmation-dialog.component";
import { DashboardBookkeeperComponent } from "./components/dashboard-bookkeeper/dashboard-bookkeeper.component";

@NgModule({
  declarations: [
    AppComponent,
    NavigationComponent,
    LoginComponent,
    DashboardComponent,
    ClientFormComponent,
    ClientListComponent,
    ZtsiTableComponent,
    ZtsiSwitchComponent,
    VehicleFormComponent,
    VehicleListComponent,
    SnackBarComponent,
    MessagesComponent,
    TableConfigurationComponent,
    ShippingLineFormComponent,
    ShippingLineListComponent,
    UserFormComponent,
    UserListComponent,
    TripFormComponent,
    TripListComponent,
    ZtsiRoleSelectComponent,
    ZtsiClientSelectComponent,
    ZtsiShippingLineSelectComponent,
    ZtsiVehiclePickerComponent,
    ZtsiContainerSizeSelectComponent,
    ZtsiPaymentTypeSelectComponent,
    DailyTripFormComponent,
    DailyTripListComponent,
    ZtsiUserSelectComponent,
    ZtsiTripSelectComponent,
    ZtsiContainerSelectComponent,
    DisableControlDirective,
    LoaderComponent,
    ZtsiSalaryTypeSelectComponent,
    ZtsiDatePickerComponent,
    SalaryFormComponent,
    SalaryHistoryComponent,
    ProfileComponent,
    ChangePasswordComponent,
    ExpenseListComponent,
    ZtsiExpenseTypeComponent,
    ZtsiPagerComponent,
    TripPaymentFormComponent,
    TripPaymentListComponent,
    CashAdvanceFormComponent,
    CashAdvanceListComponent,
    CashAdvanceFilterComponent,
    ZtsiBoolPickerComponent,
    ConnectionFailedComponent,
    UnauthorizedComponent,
    ExpenseFormComponent,
    UserFilterComponent,
    VehicleFilterComponent,
    ExpenseFilterComponent,
    TripPaymentFilterComponent,
    DailyTripFilterComponent,
    TripFilterComponent,
    SimpleTableComponent,
    NotAvailableComponent,
    BudgetFormComponent,
    BudgetListComponent,
    BudgetFilterComponent,
    CollectiblesComponent,
    ClientTripHistoryComponent,
    VehicleExpensesComponent,
    ClientCollectibleComponent,
    SalaryReportComponent,
    ParametersComponent,
    InputTypeaheadComponent,
    ZtsiBudgetTypePickerComponent,
    PayrollFormComponent,
    PayrollListComponent,
    PayrollTypeSelectComponent,
    PayrollFilterComponent,
    DailyTripTicketComponent,
    CashoutFormDialogComponent,
    ZtsiTripPickerComponent,
    ZtsiTripPickerDialogComponent,
    ZtsiTripTypeComponent,
    ZtsiDailyTripTypeComponent,
    ZtsiDeleteConfirmationDialogComponent,
    ZtsiTabsExpenseSelectComponent,
    ZtsiTabsPenaltyExpenseSelectComponent,
    TripPaymentDepositConfirmationDialogComponent,
    DashboardBookkeeperComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    HttpClientModule,
    Ng4LoadingSpinnerModule.forRoot(),
    NgDatepickerModule,
    MatDialogModule,
  ],
  providers: [
    AlertService,
    MessagesService,
    LogService,
    VehicleService,
    AuthenticationService,
    JwtInterceptorService,
    AuthGuardService,
    CommonService,
    ParameterService,
    UserService,
    ClientService,
    CacheService,
    TripService,
    ShippingLineService,
    ConfigurationService,
    DailyTripService,
    SalaryService,
    ExpenseService,
    CashAdvanceService,
    TripPaymentService,
    ReportService,
    BudgetService,
    PayrollService,
    VehicleCashoutService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptorService,
      multi: true,
    },
    DatePipe,
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    CashoutFormDialogComponent,
    ZtsiTripPickerDialogComponent,
    ZtsiDeleteConfirmationDialogComponent,
    TripPaymentDepositConfirmationDialogComponent,
  ],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts